/**
 * A signal (this tab and the session's other tabs) that the signed-in account's own row changed in a way
 * route guards cache: ProtectedRoute reads account_status once per user and
 * keeps it in local state, so a reactivation that lands after its read left
 * the guard refusing a now-active account until a reload (Codex on #1300).
 */
export const ACCOUNT_STATUS_CHANGED = 'kanfy:account-status-changed';

/** localStorage key whose write reaches the session's OTHER tabs as a
 * `storage` event. A tab whose own reactivation check found the row already
 * active (another tab won) never announces anything itself, so the winner
 * must reach it (Codex on #1300). The value is a timestamp so every write is
 * a change. */
export const ACCOUNT_STATUS_STORAGE_KEY = ACCOUNT_STATUS_CHANGED;

export function notifyAccountStatusChanged(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(ACCOUNT_STATUS_CHANGED));
  try {
    window.localStorage.setItem(ACCOUNT_STATUS_STORAGE_KEY, String(Date.now()));
  } catch {
    // Storage blocked (private mode, quota): same-tab guards were told above.
  }
}

/** Subscribe to account-status changes from this tab and from other tabs. */
export function onAccountStatusChanged(listener: () => void): () => void {
  if (typeof window === 'undefined') return () => {};
  const onStorage = (e: StorageEvent) => {
    if (e.key === ACCOUNT_STATUS_STORAGE_KEY) listener();
  };
  window.addEventListener(ACCOUNT_STATUS_CHANGED, listener);
  window.addEventListener('storage', onStorage);
  return () => {
    window.removeEventListener(ACCOUNT_STATUS_CHANGED, listener);
    window.removeEventListener('storage', onStorage);
  };
}
